import React, { Suspense } from 'react';
import type { ComponentType } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles, @atlaskit/ui-styling-standard/use-compiled -- The lightweight fallback must inject the same global extension-container styles before the full UI chunk loads.
import { Global } from '@emotion/react';

import type { ExtensionComponentProps, Parameters } from '@atlaskit/editor-common/extensions';
import type { NativeEmbedParameters } from '@atlaskit/native-embeds-common/types';
import { getParameters } from '@atlaskit/native-embeds-common/utils';
import { getNativeEmbedFacade } from '@atlassian/native-embeds-core/services/main';

import { nativeEmbedAlignmentStyles } from './styled';
import type NativeEmbedUIComponent from './ui';
import {
	NativeEmbedInitialPlaceholder,
	resolveNativeEmbedInitialLayout,
} from './ui/initial-placeholder';

type NativeEmbedExtensionParams = Parameters & NativeEmbedParameters;
export type NativeEmbedUILoaderProps = React.ComponentProps<typeof NativeEmbedUIComponent>;

const NativeEmbedUI = React.lazy<ComponentType<NativeEmbedUILoaderProps>>(() =>
	import(/* webpackChunkName: "@atlassian/native-embeds-editor-extension" */ './ui').then(
		({ default: NativeEmbedUIComponent }) => ({
			default: NativeEmbedUIComponent,
		}),
	),
);

export const NativeEmbedLoadingPlaceholder = ({
	node,
}: ExtensionComponentProps<NativeEmbedExtensionParams>): React.JSX.Element => {
	const { parameters } = node;
	const { url } = getParameters(parameters);
	const manifest = url ? getNativeEmbedFacade().getManifestByUrl(url) : undefined;

	const initialLayout = resolveNativeEmbedInitialLayout({ manifest, parameters });

	return (
		<>
			<Global styles={nativeEmbedAlignmentStyles} />
			<NativeEmbedInitialPlaceholder {...initialLayout} />
		</>
	);
};

/**
 * Synchronous shell for the editor extension renderer.
 *
 * Editor's generic async extension loading state renders `null`, which causes the native embed block to
 * collapse before the real UI chunk arrives. This small shell keeps the full UI split into its existing
 * async chunk while providing a dimensionally accurate placeholder immediately.
 */
export default function NativeEmbedUILoader(props: NativeEmbedUILoaderProps): React.JSX.Element {
	return (
		<Suspense fallback={<NativeEmbedLoadingPlaceholder {...props} />}>
			<NativeEmbedUI {...props} />
		</Suspense>
	);
}
